import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  api,
  APIError,
  isQuotaError,
  type ApplyStrategyItem,
  type AssistCategory,
  type StrategyPreset,
  type StrategyValue,
} from "@/api/client";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";

// Strategie-Panel (C2): Preset-Chips (nur global) + Freitext je Scope.
// Speichern übernimmt der Host debounced via onChange (Muster wie
// TrainerGuideCard) — das Panel selbst feuert nur Änderungen.
//
// Mit `applyTo` (nur Kategorie-Scopes) kommt der „Auf alle anwenden"-Button
// (C3) direkt unter die Textarea: /assist/apply-strategy überarbeitet alle
// Einträge der Kategorie read-only — die gespeicherte Strategie IST dabei die
// Anweisung (kein separates Instruktions-Feld). Jede Änderung wird einzeln
// per bestehendem PATCH übernommen (kein Blind-Apply), Übernommene/
// Verworfene verschwinden aus der Review-Liste.

export type StrategyScope = "global" | "ilo" | "tla" | "at";

export interface ApplyToConfig {
  courseId: string;
  category: AssistCategory;
  label: string; // Kategorie-Label für Button/Tooltips, z. B. t("editor.ilosLabel")
  hasEntries: boolean; // Kategorie hat Einträge (sonst ist der Button disabled)
  onApply: (entryId: string, newText: string) => Promise<void>;
}

const SCOPE_KEYS: Record<
  StrategyScope,
  { key: keyof Omit<StrategyValue, "presets">; labelKey: string; placeholderKey: string; hintKey: string }
> = {
  global: {
    key: "global_text",
    labelKey: "strategy.global.label",
    placeholderKey: "strategy.global.placeholder",
    hintKey: "strategy.global.hint",
  },
  ilo: {
    key: "ilo_text",
    labelKey: "strategy.ilo.label",
    placeholderKey: "strategy.ilo.placeholder",
    hintKey: "strategy.ilo.hint",
  },
  tla: {
    key: "tla_text",
    labelKey: "strategy.tla.label",
    placeholderKey: "strategy.tla.placeholder",
    hintKey: "strategy.tla.hint",
  },
  at: {
    key: "at_text",
    labelKey: "strategy.at.label",
    placeholderKey: "strategy.at.placeholder",
    hintKey: "strategy.at.hint",
  },
};

export default function StrategyPanel({
  value,
  onChange,
  scope,
  onSave,
  applyTo,
}: {
  value: StrategyValue;
  onChange: (v: StrategyValue) => void;
  scope: StrategyScope;
  onSave?: () => void;
  applyTo?: ApplyToConfig;
}) {
  const { t } = useTranslation();
  const cfg = SCOPE_KEYS[scope];
  const [presets, setPresets] = useState<StrategyPreset[]>([]);

  // Apply-Strategie-State (nur genutzt, wenn applyTo gesetzt ist)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);
  const [items, setItems] = useState<ApplyStrategyItem[] | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);

  // Preset-Katalog nur im globalen Scope nötig (Chips gibt es nur dort).
  useEffect(() => {
    if (scope !== "global") return;
    api.strategyPresets().then(setPresets).catch(() => setPresets([]));
  }, [scope]);

  function togglePreset(id: string) {
    const next = value.presets.includes(id)
      ? value.presets.filter((p) => p !== id)
      : [...value.presets, id];
    onChange({ ...value, presets: next });
    onSave?.();
  }

  // Guard: ohne JEDE Strategie (Kategorie-Text, globaler Text, Presets) würde
  // der Apply-Call die KI ohne Anweisung laufen lassen → Button sperren.
  const hasAnyStrategy =
    value[cfg.key].trim().length > 0 ||
    value.global_text.trim().length > 0 ||
    value.presets.length > 0;
  const applyDisabled = !applyTo?.hasEntries || !hasAnyStrategy;
  const applyTitle = !applyTo?.hasEntries
    ? t("editor.strategyDisabledTitle", { label: applyTo?.label })
    : !hasAnyStrategy
      ? t("editor.strategyNoStrategyHint")
      : t("editor.strategyTitle");

  async function runApply() {
    if (!applyTo) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.applyStrategy({
        course_id: applyTo.courseId,
        category: applyTo.category,
      });
      setItems(res.items);
      if (res.items.length === 0) {
        setError(t("editor.strategyNone"));
      }
    } catch (e) {
      if (isQuotaError(e)) {
        setQuota(e.data);
      } else {
        setError(
          e instanceof APIError
            ? e.status === 429
              ? t("common.aiLimit")
              : e.detail
            : t("editor.strategyFailed"),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  function dismiss(itemId: string) {
    setItems((prev) => (prev ? prev.filter((i) => i.id !== itemId) : prev));
  }

  async function applyOne(item: ApplyStrategyItem) {
    if (!applyTo) return;
    setApplyingId(item.id);
    setError(null);
    try {
      await applyTo.onApply(item.id, item.new_text);
      dismiss(item.id);
    } catch (e) {
      setError(e instanceof APIError ? e.detail : t("common.applyFailed"));
    } finally {
      setApplyingId(null);
    }
  }

  const compact = scope !== "global";

  return (
    <div
      className={`bg-ca-card rounded-2xl border border-ca-surface-border ${compact ? "p-4 mb-6" : "p-6"}`}
    >
      <h3 className="text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-1">
        {t(cfg.labelKey)}
      </h3>
      <p className="text-[11px] text-ca-surface-muted mb-3">{t(cfg.hintKey)}</p>

      {scope === "global" && presets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {presets.map((p) => {
            const active = value.presets.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => togglePreset(p.id)}
                title={p.prompt_hint}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
                  active
                    ? "border-ca-accent bg-ca-accent text-ca-brand"
                    : "border-ca-surface-border bg-ca-card text-ca-surface-ink hover:border-ca-accent"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}

      <textarea
        value={value[cfg.key]}
        onChange={(e) => onChange({ ...value, [cfg.key]: e.target.value })}
        placeholder={t(cfg.placeholderKey)}
        rows={2}
        className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted text-sm outline-none focus:border-ca-accent resize-y"
      />

      {applyTo && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={runApply}
            disabled={applyDisabled || busy}
            title={applyTitle}
            className="px-4 py-2 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest disabled:opacity-40 transition"
          >
            {busy ? t("common.aiWorking") : t("editor.strategyApply", { label: applyTo.label })}
          </button>
        </div>
      )}

      {applyTo && quota !== null && (
        <div className="mt-2">
          <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
        </div>
      )}
      {applyTo && error && <p className="mt-2 text-xs text-verdict-red">{error}</p>}

      {applyTo && items && items.length > 0 && (
        <div className="mt-4 space-y-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-ca-surface-muted">
            {t("editor.strategyReviewHint")}
          </p>
          {items.map((item) => (
            <div key={item.id} className="p-3 rounded-xl border border-ca-surface-border bg-ca-card">
              <p className="text-xs text-ca-surface-muted whitespace-pre-wrap">
                <span className="font-bold">{t("common.before")}: </span>
                {item.old_text || t("common.empty")}
              </p>
              <p className="mt-2 text-sm text-ca-surface-ink whitespace-pre-wrap">
                <span className="font-bold">{t("common.new")}: </span>
                {item.new_text}
              </p>
              {item.rationale && (
                <p className="mt-1 text-[11px] text-ca-surface-muted italic">→ {item.rationale}</p>
              )}
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => applyOne(item)}
                  disabled={applyingId !== null}
                  className="px-3 py-1 rounded-md bg-ca-accent text-ca-brand text-[11px] font-black uppercase tracking-widest disabled:opacity-40 transition"
                >
                  {applyingId === item.id ? t("common.saving") : t("common.apply")}
                </button>
                <button
                  type="button"
                  onClick={() => dismiss(item.id)}
                  className="px-3 py-1 rounded-md border border-ca-surface-border text-ca-surface-muted text-[11px] font-semibold hover:text-ca-surface-ink transition"
                >
                  {t("common.discard")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
